import React, { useState, FormEvent } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { acceptablepayment } from '@/utils/imagepath';

interface StripeModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  stripePromise: Stripe | null;
  onSuccess: (data: any) => void;
  transactionId: string; // Added to receive transactionId from parent component
  setupIntentClientSecret?: string | null; // Optional setup intent client secret
}

const StripeCheckoutForm: React.FC<{
  amount: number;
  onClose: () => void;
  onSuccess: (data: any) => void;
  transactionId: string; // Pass transactionId from parent
  setupIntentClientSecret?: string | null; // Optional setup intent client secret
}> = ({ amount, onClose, onSuccess, transactionId, setupIntentClientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setIsProcessing(true);

    try {
      // If setup intent is provided, use it to save card for future payments
      if (setupIntentClientSecret) {
        const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
          setupIntentClientSecret,
          {
            payment_method: {
              card: cardElement,
            },
          }
        );

        if (setupError) {
          console.error('[Stripe Setup Error]', setupError.message);
          toast.error(setupError.message);
          setIsProcessing(false);
          return;
        }

        // Setup intent succeeded, return payment method
        onSuccess({
          transactionId,
          paymentMethod: {
            id: setupIntent.payment_method,
          },
          setupIntent: {
            id: setupIntent.id,
          },
        });
        onClose();
      } else {
        // Regular payment flow - create token for immediate payment
        const { token, error } = await stripe.createToken(cardElement);

        if (error) {
          console.error('[Stripe Error]', error.message);
          toast.error(error.message);
          setIsProcessing(false);
        } else {
          onSuccess({ transactionId, token });
          onClose();
        }
      }
    } catch (error: any) {
      console.error('[Stripe Error]', error);
      toast.error(error.message || 'An error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={formStyles}>
      <h2 style={headerStyles}>
        {setupIntentClientSecret ? 'Save Payment Method' : `Pay ${amount.toFixed(2)}`}
      </h2>
      {setupIntentClientSecret && (
        <p style={{ color: '#666', fontSize: '14px' }}>
          Your card will be saved for future payments
        </p>
      )}
      <div style={cardWrapperStyles}>
        <CardElement options={cardElementOptions} />
      </div>
      <span className='payment-collect-info'>Note : By clicking 'Place Order' your card will be verified by Stripe and no amount will be debited for now.</span>
      <div style={buttonWrapperStyles}>
        <button type="button" onClick={onClose} style={cancelButtonStyles}>
          Cancel
        </button>
        <button type="submit" disabled={isProcessing} style={payButtonStyles}>
          {isProcessing
            ? 'Processing…'
            : setupIntentClientSecret
            ? 'Proceed'
            : 'Pay Now'}
        </button>
      </div>
      <div className="acc-payments">
        <img
          src={acceptablepayment}
          className="img-fluid"
          alt="Payment Methods"
        />
      </div>
      <div className="alert-info">
        <p>
          We are using strong SSL ciphers and we won't save
          any critical card information. Payments are powered
          by PCI-certified{" "}
          <a
            target="_blank"
            href="https://stripe.com/docs/security/stripe"
          >
            Stripe Payment Gateway
          </a>
          . If you have any questions, or encounter any
          issues, please{" "}
          <a target="_blank" href="tel:+442086388690">
            let us know.
          </a>
        </p>
      </div>
    </form>
  );
};

const StripeModal: React.FC<StripeModalProps> = ({
  isOpen,
  onClose,
  amount,
  stripePromise,
  onSuccess,
  transactionId, // Receive the transactionId from the parent component
  setupIntentClientSecret, // Optional setup intent client secret
}) => {
  if (!isOpen) return null;

  // const stripePromise = loadStripe(stripeKey);

  return (
    <div style={overlayStyles}>
      <div style={modalStyles}>
        <Elements 
          stripe={stripePromise}
          options={setupIntentClientSecret ? {
            clientSecret: setupIntentClientSecret,
          } : undefined}
        >
          <StripeCheckoutForm
            amount={amount}
            onClose={onClose}
            onSuccess={onSuccess}
            transactionId={transactionId} // Pass the transactionId to StripeCheckoutForm
            setupIntentClientSecret={setupIntentClientSecret} // Pass setup intent if provided
          />
        </Elements>
      </div>
    </div>
  );
};

// Styles
const overlayStyles = {
  position: 'fixed',
  inset: '0',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  overflowY: 'scroll',
};

const modalStyles = {
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.1)',
  padding: '40px 30px',
  width: '100%',
  maxWidth: '600px',
  transition: 'all 0.3s ease',
  textAlign: 'center',
};

const formStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const headerStyles = {
  fontSize: '24px',
  fontWeight: '700',
  color: '#333',
};

const cardElementOptions = {
  style: {
    base: {
      iconColor: '#5E9B00',
      color: '#222',
      fontWeight: 500,
      fontSize: '18px',
      fontFamily: 'inherit',
      lineHeight: '1.5',
      letterSpacing: '0.03em',
      padding: '0',
      backgroundColor: '#fff',
      '::placeholder': {
        color: '#aaa',
        fontSize: '16px',
      },
      border: 'none',
      boxShadow: 'none',
    },
    complete: {
      iconColor: '#5E9B00',
      color: '#222',
    },
    invalid: {
      iconColor: '#e53e3e',
      color: '#e53e3e',
    },
  },
  hidePostalCode: true,
};

const cardWrapperStyles = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  padding: '18px 20px',
  transition: 'box-shadow 0.2s',
};

const buttonWrapperStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '15px',
};

const cancelButtonStyles = {
  backgroundColor: '#f5f5f5',
  color: '#333',
  padding: '14px 24px',
  borderRadius: '8px',
  border: '1px solid #ddd',
  cursor: 'pointer',
  fontSize: '16px',
  transition: 'background-color 0.3s, transform 0.2s',
  width: '48%',
};

const payButtonStyles = {
  backgroundColor: '#5E9B00',
  color: '#fff',
  padding: '14px 24px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  transition: 'background-color 0.3s, transform 0.2s',
  width: '48%',
};

export default StripeModal;
